import { useCallback, useEffect, useReducer, useRef } from 'react';

import { WebSpeechToTextManager } from '../effects/speech-to-text/WebSpeechToTextManager';
import {
	VoiceDictationResultAction,
	type VoiceDictationState,
	type WebSpeechToTextAPIAction,
} from '../effects/speech-to-text/WebSpeechToTextModels';
import { voiceDictationReducer } from '../effects/voiceDictationReducer';

export const useVoiceDictation = (
	onTranscriptUpdate: (transcript: string) => void,
	cloudID: string,
	audioBitsPerSecond = 16000,
	sampleRate = 24000,
	timeSlice = 1000,
	maxRecordingDurationMs = 60000,
): [VoiceDictationState, { startRecording: () => void; stopRecording: () => void }] => {
	const [state, dispatch] = useReducer(voiceDictationReducer, {
		action: VoiceDictationResultAction.Uninitialized,
		transcription: null,
	});

	const onTranscription = useCallback(
		(action: WebSpeechToTextAPIAction) => {
			dispatch(action);
		},
		[dispatch],
	);

	const speechToTextManager = useRef<WebSpeechToTextManager>(
		new WebSpeechToTextManager(onTranscription, cloudID, sampleRate, audioBitsPerSecond, timeSlice),
	);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const clearRecordingTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (state.transcription) {
			onTranscriptUpdate(state.transcription);
		}
	}, [state.transcription, onTranscriptUpdate]);

	const startRecording = useCallback(() => {
		clearRecordingTimeout();

		speechToTextManager.current.startRecording();

		timeoutRef.current = setTimeout(() => {
			clearRecordingTimeout();
			speechToTextManager.current.stopRecording();
		}, maxRecordingDurationMs);
	}, [maxRecordingDurationMs, clearRecordingTimeout]);

	const stopRecording = useCallback(() => {
		clearRecordingTimeout();
		speechToTextManager.current.stopRecording();
	}, [clearRecordingTimeout]);

	useEffect(() => {
		const manager = speechToTextManager.current;
		return () => {
			clearRecordingTimeout();
			manager.stopRecording();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return [state, { startRecording, stopRecording }];
};
